import { Capacitor } from '@capacitor/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { Motion } from '@capacitor/motion';
import clsx from 'clsx';
import { Map, Marker } from 'mapbox-gl';

import classes from './styles.module.css';

function isCapacitorNative() {
  return Capacitor.isNativePlatform();
}

/** GPS heading is only meaningful while moving; compass handles stationary heading. */
const GPS_HEADING_MIN_SPEED_MPS = 1;

function getGpsHeadingIfReliable(coords: Position['coords']): number | null {
  const { heading, speed } = coords;
  if (typeof heading !== 'number' || !Number.isFinite(heading) || heading < 0) {
    return null;
  }
  if (typeof speed !== 'number' || !Number.isFinite(speed) || speed < GPS_HEADING_MIN_SPEED_MPS) {
    return null;
  }
  return heading;
}

async function requestWebOrientationPermissionIfNeeded() {
  type IOSPermissionEvent = {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  };

  const eventWithPermission = (
    globalThis as unknown as { DeviceOrientationEvent?: IOSPermissionEvent }
  ).DeviceOrientationEvent;

  if (!eventWithPermission?.requestPermission) return;

  try {
    await eventWithPermission.requestPermission();
  } catch {
    // Ignore: permission prompt can be blocked/cancelled by the user.
  }
}

enum MapboxGeolocateState {
  Waiting = 'waiting',
  Active = 'active',
  Disabled = 'disabled',
}

// mapboxgl-ctrl-geolocate mapboxgl-ctrl-geolocate-waiting mapboxgl-ctrl-geolocate-active
export class MapboxGeolocateControl {
  _map: Map | undefined;
  _container: HTMLElement | undefined;
  _marker: Marker | undefined;
  button: HTMLButtonElement | undefined;
  spanIcon: HTMLSpanElement | undefined;

  _heading = 0;

  isGeolocating = false;
  isFollowing = false; // kept for compatibility, but we don't follow continuously anymore
  watchId: string | undefined;
  motionListenerHandle:
    | {
        remove: () => Promise<void>;
      }
    | undefined;
  latestPosition: Position | undefined;
  zoomAfterInitRequested = false;
  deviceOrientationHandler:
    | ((event: Event) => void)
    | undefined;

  geolocateOnInit: boolean;

  onPositionChange: ((position: Position) => void) | undefined;

  private readonly handleStartGeolocate = () => {
    this.startGeolocate();
  };

  private readonly handleTouchEnd = () => {
    this.onTouchEnd();
  };

  constructor(options: {
    onPositionChange?: (position: Position) => void;
    geolocateOnInit?: boolean;
  }) {
    this.onPositionChange = options.onPositionChange;
    this.geolocateOnInit = options.geolocateOnInit ?? false;
  }

  onTouchEnd() {
    // stop following user location on user interaction
    this.isFollowing = false;
    this.setButtonState(MapboxGeolocateState.Disabled);
  }

  onAdd(map: Map) {
    this._map = map;
    this._map.on('touchend', this.handleTouchEnd);
    // control container
    this._container = document.createElement('div');

    // control button
    this.button = document.createElement('button');
    this.button.className = 'mapboxgl-ctrl-geolocate';
    this.button.ariaLabel = 'Find my location';
    // this.startGeolocate();
    this.button.addEventListener('click', this.handleStartGeolocate);

    // control icon
    this.spanIcon = document.createElement('span');
    this.spanIcon.className = 'mapboxgl-ctrl-icon';
    this.spanIcon.title = 'Find my location';
    this.spanIcon.ariaHidden = 'true';

    // add to dom
    this.button.appendChild(this.spanIcon);
    this._container.appendChild(this.button);
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    if (this.geolocateOnInit) {
      // Start GPS watch immediately so distance/proximity UI stays responsive,
      // without "following" the user on every update.
      void this.geolocate({ follow: false, zoomOnFirst: false });
    }
    return this._container;
  }

  onRemove() {
    this._container?.parentNode?.removeChild(this._container);
    this.button?.removeEventListener('click', this.handleStartGeolocate);
    this._map?.off('touchend', this.handleTouchEnd);
    void this.motionListenerHandle?.remove();
    this.motionListenerHandle = undefined;
    if (this.deviceOrientationHandler) {
      window.removeEventListener('deviceorientation', this.deviceOrientationHandler, true);
      this.deviceOrientationHandler = undefined;
    }
    void this.clearWatch();
    this.removeLocationMarker();

    this._map = undefined;
  }

  removeLocationMarker() {
    if (this._marker) {
      this._marker.remove();
      this._marker = undefined;
    }

    // Orphaned markers can remain when concurrent geolocate calls race.
    this._map
      ?.getContainer()
      .querySelectorAll('.mapboxgl-user-location')
      .forEach((element) => {
        element.closest('.mapboxgl-marker')?.remove();
      });
  }

  setButtonState(state: MapboxGeolocateState) {
    if (this.button) {
      switch (state) {
        case MapboxGeolocateState.Waiting:
          this.button.className = `mapboxgl-ctrl-geolocate mapboxgl-ctrl-geolocate-waiting`;
          break;
        case MapboxGeolocateState.Active:
          this.button.className = `mapboxgl-ctrl-geolocate mapboxgl-ctrl-geolocate-active`;
          break;
        case MapboxGeolocateState.Disabled:
          this.button.className = `mapboxgl-ctrl-geolocate`;
          break;
        default:
          break;
      }
    }
  }

  startGeolocate() {
    // iOS requires a user gesture for orientation permission. Ensure heading
    // listeners are (re)started from this click path.
    void this.ensureOrientationTracking(true);

    // Button behavior: only zoom to the latest user location.
    // We never enable continuous "following" because the UI needs stable map
    // interactions (and we already track in background).
    if (this.latestPosition) {
      this.setButtonState(MapboxGeolocateState.Waiting);
      this.zoomToLatestPosition();
      this.setButtonState(MapboxGeolocateState.Active);
      return;
    }

    if (this.isGeolocating) {
      // Initial GPS request is still pending; zoom as soon as we have a position.
      this.zoomAfterInitRequested = true;
      this.setButtonState(MapboxGeolocateState.Waiting);
      return;
    }

    this.setButtonState(MapboxGeolocateState.Waiting);
    void this.geolocate({ follow: false, zoomOnFirst: true });
  }

  zoomToLatestPosition() {
    if (!this._map || !this.latestPosition) return;
    const { longitude, latitude } = this.latestPosition.coords;
    this._map.flyTo({
      center: [longitude, latitude],
      // keep current zoom to avoid weird jumps
      zoom: this._map.getZoom(),
      bearing: this._heading,
      animate: true,
      curve: 1,
    });
  }

  async geolocate(opts: { follow: boolean; zoomOnFirst: boolean }) {
    if (this.watchId) {
      if (opts.zoomOnFirst) this.zoomToLatestPosition();
      return;
    }
    if (this.isGeolocating) {
      if (opts.zoomOnFirst) this.zoomAfterInitRequested = true;
      return;
    }

    this.isGeolocating = true;

    let coordinates: Position | undefined;

    try {
      coordinates = await Geolocation.getCurrentPosition();
      this.setButtonState(MapboxGeolocateState.Active);
      this.isFollowing = opts.follow;
    } catch (error) {
      this.isGeolocating = false;
      this.setButtonState(MapboxGeolocateState.Disabled);
      return;
    }

    await this.ensureOrientationTracking(false);

    this.removeLocationMarker();

    const uel = document.createElement('div');
    uel.id = 'mapboxgl-user-location';
    uel.className = clsx(
      classes.locationDot,
      'mapboxgl-user-location mapboxgl-marker mapboxgl-marker-anchor-center mapboxgl-user-location-show-heading',
    );
    const locationDot = document.createElement('div');
    locationDot.classList.add('mapboxgl-user-location-dot');
    uel.appendChild(locationDot);

    const locationHeading = document.createElement('div');
    locationHeading.classList.add('mapboxgl-user-location-heading');
    uel.appendChild(locationHeading);

    if (this._map) {
      this.latestPosition = coordinates;
      this.onPositionChange?.(coordinates);

      this._marker = new Marker({
        element: uel,
        rotationAlignment: 'map',
        pitchAlignment: 'map',
      })
        .setLngLat([coordinates.coords.longitude, coordinates.coords.latitude])
        .addTo(this._map);

      const shouldZoomOnFirst = opts.zoomOnFirst || this.zoomAfterInitRequested;
      this.zoomAfterInitRequested = false;
      if (shouldZoomOnFirst) {
        this.latestPosition = coordinates;
        this.zoomToLatestPosition();
      }

      try {
        this.watchId = await Geolocation.watchPosition(
          {
            enableHighAccuracy: true,
            timeout: 1500,
            minimumUpdateInterval: 500,
          },
          (position, err) => {
            if (position) {
              this.latestPosition = position;
              const gpsHeading = getGpsHeadingIfReliable(position.coords);
              if (gpsHeading !== null) {
                this._heading = gpsHeading;
                this.updateMarkerRotation();
              }
              this._marker?.setLngLat([
                position.coords.longitude,
                position.coords.latitude,
              ]);
              if (this.onPositionChange) this.onPositionChange(position);
            }
          },
        );
      } catch (e) {
        console.error(e);
      }
    }
  }

  updateMarkerRotation() {
    if (this._marker) {
      this._marker.setRotation(this._heading);
    }
  }

  async ensureOrientationTracking(fromUserGesture: boolean) {
    if (fromUserGesture) {
      await requestWebOrientationPermissionIfNeeded();
    }

    // On web/PWA, Capacitor Motion is a thin wrapper around `deviceorientation`.
    // Register only one source to avoid duplicate heading updates (Android flicker).
    if (isCapacitorNative()) {
      if (!this.motionListenerHandle) {
        this.motionListenerHandle = await Motion.addListener('orientation', (e) => {
          const alpha = typeof e.alpha === 'number' ? e.alpha : null;
          if (alpha === null) return;
          const trueHeading = (360 - alpha) % 360;
          this._heading = trueHeading;
          this.updateMarkerRotation();
        });
      }
      return;
    }

    if (!this.deviceOrientationHandler) {
      this.deviceOrientationHandler = (event: Event) => {
        const evt = event as Event & {
          alpha?: number | null;
          webkitCompassHeading?: number;
        };

        const iosHeading =
          typeof evt.webkitCompassHeading === 'number'
            ? evt.webkitCompassHeading
            : null;
        const alphaHeading =
          typeof evt.alpha === 'number' ? (360 - evt.alpha) % 360 : null;
        const nextHeading = iosHeading ?? alphaHeading;

        if (nextHeading === null) return;
        this._heading = nextHeading;
        this.updateMarkerRotation();
      };
      window.addEventListener('deviceorientation', this.deviceOrientationHandler, true);
    }
  }

  async clearWatch() {
    if (this.watchId) {
      await Geolocation.clearWatch({ id: this.watchId });
      this.watchId = undefined;
    }
  }
}

export default MapboxGeolocateControl;
